import TamboAI from "@tambo-ai/typescript-sdk";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { renderHook, act } from "@testing-library/react";
import React from "react";
import { z } from "zod";
import {
  useTamboClient,
  useTamboQueryClient,
} from "../../providers/tambo-client-provider";
import { useTamboRegistry } from "../../providers/tambo-registry-provider";
import { useTamboContextHelpers } from "../../providers/tambo-context-helpers-provider";
import { useStreamState, useThreadManagement } from "./tambo-v1-stream-context";
import { TamboProvider, useTamboConfig } from "./tambo-v1-provider";

// Module-level QueryClient for tests - created lazily
let testQueryClient: QueryClient | null = null;

// Mock the client provider to capture the apiKey
jest.mock("../../providers/tambo-client-provider", () => {
  return {
    useTamboClient: jest.fn(),
    useTamboQueryClient: jest.fn(),
    TamboClientProvider: jest.fn(
      ({ children }: { children: React.ReactNode }): React.ReactNode =>
        children,
    ),
  };
});

// Mock MCP providers to avoid TamboClientContext dependency
jest.mock("../../providers/tambo-mcp-token-provider", () => ({
  TamboMcpTokenProvider: ({
    children,
  }: {
    children: React.ReactNode;
  }): React.ReactNode => children,
}));

jest.mock("../../mcp/tambo-mcp-provider", () => ({
  TamboMcpProvider: ({
    children,
  }: {
    children: React.ReactNode;
  }): React.ReactNode => children,
}));

// Mock auth state to avoid TamboClientContext dependency
jest.mock("../hooks/use-tambo-v1-auth-state", () => ({
  useTamboAuthState: () => ({
    status: "identified",
    source: "userKey",
  }),
}));

// Mock useTamboSendMessage to avoid complex dependencies
jest.mock("../hooks/use-tambo-v1-send-message", () => ({
  useTamboSendMessage: jest.fn(() => ({
    mutateAsync: jest.fn(),
    mutate: jest.fn(),
    isPending: false,
    isError: false,
    error: null,
    isSuccess: false,
    reset: jest.fn(),
  })),
}));

describe("TamboProvider", () => {
  const mockFetch: typeof fetch = async (..._args) => {
    throw new Error("fetch not implemented");
  };

  const mockClient = new TamboAI({
    apiKey: "test-api-key",
    fetch: mockFetch,
  });

  beforeEach(() => {
    // Create a fresh QueryClient for each test
    testQueryClient = new QueryClient({
      defaultOptions: {
        queries: { retry: false },
        mutations: { retry: false },
      },
    });

    jest.mocked(useTamboClient).mockReturnValue(mockClient);
    jest.mocked(useTamboQueryClient).mockReturnValue(testQueryClient);

    // Mock TamboClientProvider to wrap children with QueryClientProvider
    const { TamboClientProvider } = jest.requireMock(
      "../../providers/tambo-client-provider",
    );
    jest
      .mocked(TamboClientProvider)
      .mockImplementation(({ children }: { children: React.ReactNode }) => (
        <QueryClientProvider client={testQueryClient!}>
          {children}
        </QueryClientProvider>
      ));
  });

  it("provides access to registry context", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.componentList).toBeDefined();
    expect(result.current.toolRegistry).toBeDefined();
    expect(typeof result.current.registerComponent).toBe("function");
    expect(typeof result.current.registerTool).toBe("function");
  });

  it("provides access to stream context with placeholder thread ready", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(() => useStreamState(), { wrapper });

    expect(result.current.threadMap).toBeDefined();
    // Initial state has placeholder thread for optimistic UI
    expect(result.current.currentThreadId).toBe("placeholder");
    expect(result.current.threadMap.placeholder).toBeDefined();
  });

  it("manages threads via useThreadManagement", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(
      () => ({
        state: useStreamState(),
        management: useThreadManagement(),
      }),
      { wrapper },
    );

    // Initially has placeholder thread for optimistic UI
    expect(result.current.state.currentThreadId).toBe("placeholder");

    // Initialize and switch to a different thread
    act(() => {
      result.current.management.initThread("thread_123");
      result.current.management.switchThread("thread_123");
    });

    expect(result.current.state.currentThreadId).toBe("thread_123");
    expect(result.current.state.threadMap.thread_123).toBeDefined();
  });

  it("provides access to query client via useTamboQueryClient", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(() => useTamboQueryClient(), { wrapper });

    expect(result.current).toBeInstanceOf(QueryClient);
  });

  it("registers components when provided", () => {
    const TestComponent = () => <div>Test</div>;
    const propsSchema = z.object({
      title: z.string().describe("The title"),
    });
    const components = [
      {
        name: "TestComponent",
        description: "A test component",
        component: TestComponent,
        propsSchema,
      },
    ];

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" components={components}>
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.componentList.TestComponent).toBeDefined();
    expect(result.current.componentList.TestComponent.name).toBe(
      "TestComponent",
    );
  });

  it("registers tools when provided", () => {
    const inputSchema = z.object({
      query: z.string().describe("Search query"),
    });
    const outputSchema = z.string().describe("Result string");
    const tools = [
      {
        name: "testTool",
        description: "A test tool",
        tool: async () => "result",
        inputSchema,
        outputSchema,
      },
    ];

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" tools={tools}>
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.toolRegistry.testTool).toBeDefined();
    expect(result.current.toolRegistry.testTool.name).toBe("testTool");
  });

  it("registers MCP servers when provided", () => {
    const mcpServers = [
      { url: "https://mcp.example.com", name: "Example MCP" },
    ];

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" mcpServers={mcpServers}>
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.mcpServerInfos).toHaveLength(1);
    expect(result.current.mcpServerInfos[0].url).toBe(
      "https://mcp.example.com",
    );
  });

  it("provides onCallUnregisteredTool to registry", () => {
    const onCallUnregisteredTool = jest
      .fn()
      .mockResolvedValue("fallback result");

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider
        apiKey="test-api-key"
        onCallUnregisteredTool={onCallUnregisteredTool}
      >
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.onCallUnregisteredTool).toBe(onCallUnregisteredTool);
  });

  it("registers static resources when provided", () => {
    const resources = [
      {
        uri: "resource://test/example",
        name: "Test Resource",
        description: "A test resource",
        mimeType: "text/plain",
      },
    ];

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" resources={resources}>
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.resources).toHaveLength(1);
    expect(result.current.resources[0].uri).toBe("resource://test/example");
    expect(result.current.resources[0].name).toBe("Test Resource");
  });

  it("registers resource source when listResources and getResource provided", () => {
    const listResources = jest.fn().mockResolvedValue({ resources: [] });
    const getResource = jest.fn().mockResolvedValue({ contents: [] });

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider
        apiKey="test-api-key"
        listResources={listResources}
        getResource={getResource}
      >
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboRegistry(), { wrapper });

    expect(result.current.resourceSource).toBeDefined();
    expect(result.current.resourceSource?.listResources).toBe(listResources);
    expect(result.current.resourceSource?.getResource).toBe(getResource);
  });

  it("provides userKey via useTamboConfig", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" userKey="my-user-key">
        {children}
      </TamboProvider>
    );

    const { result } = renderHook(() => useTamboConfig(), { wrapper });

    expect(result.current.userKey).toBe("my-user-key");
  });

  it("returns undefined userKey from useTamboConfig when no userKey provided", () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(() => useTamboConfig(), { wrapper });

    expect(result.current.userKey).toBeUndefined();
  });

  it("provides context helpers via useTamboContextHelpers hook", async () => {
    const contextHelpers = {
      getUserName: () => "Test User",
      getCurrentTime: () => new Date().toISOString(),
    };

    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key" contextHelpers={contextHelpers}>
        {children}
      </TamboProvider>
    );

    const { result, rerender } = renderHook(() => useTamboContextHelpers(), {
      wrapper,
    });

    // Helpers are registered via useEffect, so we need to trigger a rerender
    await act(async () => {
      rerender();
    });

    const helpers = result.current.getContextHelpers();
    expect(helpers.getUserName).toBe(contextHelpers.getUserName);
    expect(helpers.getCurrentTime).toBe(contextHelpers.getCurrentTime);
  });

  it("returns only interactables contextHelper when none explicitly provided", async () => {
    const wrapper = ({ children }: { children: React.ReactNode }) => (
      <TamboProvider apiKey="test-api-key">{children}</TamboProvider>
    );

    const { result } = renderHook(() => useTamboContextHelpers(), { wrapper });

    const helpers = result.current.getContextHelpers();
    // TamboInteractableProvider registers "interactables" and TamboContextAttachmentProvider registers "contextAttachments"
    expect(Object.keys(helpers)).toEqual([
      "interactables",
      "contextAttachments",
    ]);
  });
});
